// ---------------------------------------------------------------------------
// Financeiro › Mapa de Caixa — ORDENAÇÃO e PAGINAÇÃO da tabela da Consulta.
//
// Funções PURAS (sem React, sem fetch): a tela só desenha. Existem separadas porque o
// que erra aqui é a DECISÃO — onde o valor vazio cai, qual linha vem antes num empate —
// e isso se testa sem abrir navegador (`mapa-caixa-tabela.test.js`, ao lado).
//
// ⚠️ Ordena e pagina SÓ as linhas carregadas (o servidor devolve no máximo 2.000). Com o
//    aviso de "truncado" aceso, "maior quebra" vale só para essas linhas — o aviso já
//    existe na tela e é quem avisa. Ordenar no servidor ficou fora do escopo (DEM-00070).
// ---------------------------------------------------------------------------

/** Ordem de abertura da tela: a mesma que o servidor já devolve (data mais recente primeiro). */
export const ORDEM_PADRAO = { chave: 'data', sentido: 'desc' };

/** Tamanhos de página oferecidos; o primeiro é o padrão. */
export const TAMANHOS_PAGINA = [25, 50, 100];

/**
 * A ordem depois de um clique no cabeçalho de `chave`.
 * Mesma coluna: inverte o sentido. Coluna nova: começa CRESCENTE (previsível — o
 * segundo clique inverte). A Data é a única que abre decrescente, e isso é só o
 * estado inicial da tela (`ORDEM_PADRAO`), não regra de clique.
 */
export function proximaOrdem(atual, chave) {
    if (atual && atual.chave === chave) {
        return { chave, sentido: atual.sentido === 'asc' ? 'desc' : 'asc' };
    }
    return { chave, sentido: 'asc' };
}

// Vazio = sem valor para comparar. ⚠️ `0` NÃO é vazio: zero é "preenchi, e é zero".
const vazio = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));

/** Compara dois valores NÃO vazios: número por valor, texto sem diferenciar acento/caixa. */
function comparar(a, b) {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base', numeric: true });
}

/**
 * Cópia ORDENADA das linhas (a original não é tocada).
 *
 * ⚠️⚠️ VALOR VAZIO VAI SEMPRE PARA O FIM, nos DOIS sentidos. Tratar vazio como zero
 *    faria um mapa SEM aquele campo aparecer no topo de "menor quebra" — e vazio se
 *    lê como "conferido e bateu". É a mesma regra do módulo inteiro: `null` ≠ 0.
 *
 * ⚠️ Desempate FIXO (data mais recente, depois loja A→Z), independente do sentido
 *    pedido: sem ele a ordem dos iguais muda a cada clique e a tabela "pula".
 *
 * @param {object[]} linhas  as linhas da consulta (`dados.linhas`)
 * @param {{chave:string, sentido:'asc'|'desc'}} ordem
 */
export function ordenarLinhas(linhas, ordem) {
    const { chave, sentido } = ordem || ORDEM_PADRAO;
    const fator = sentido === 'desc' ? -1 : 1;

    return [...(linhas || [])].sort((x, y) => {
        const a = x[chave];
        const b = y[chave];
        const va = vazio(a);
        const vb = vazio(b);

        if (va !== vb) return va ? 1 : -1; // vazio sempre depois, sem multiplicar pelo fator
        if (!va) {
            const c = comparar(a, b);
            if (c !== 0) return c * fator;
        }
        // Empate (ou ambos vazios): data mais recente primeiro, depois loja.
        const porData = comparar(y.data, x.data);
        if (porData !== 0) return porData;
        return comparar(x.loja, y.loja);
    });
}

/**
 * Uma página das linhas, já ordenadas.
 * Página fora do intervalo (ex.: a busca trouxe menos linhas) é CORRIGIDA, não erro.
 * Tamanho que não esteja em `TAMANHOS_PAGINA` cai no padrão.
 *
 * @returns {{itens:object[], pagina:number, totalPaginas:number, de:number, ate:number, total:number}}
 *          `de`/`ate` são posições de 1 a N para o contador ("Mostrando 1–25 de N"); 0/0 se vazio.
 */
export function paginar(linhas, pagina, tamanho) {
    const lista = linhas || [];
    const tam = TAMANHOS_PAGINA.includes(tamanho) ? tamanho : TAMANHOS_PAGINA[0];
    const total = lista.length;
    const totalPaginas = Math.max(1, Math.ceil(total / tam));
    const atual = Math.min(Math.max(1, Math.floor(Number(pagina)) || 1), totalPaginas);
    const inicio = (atual - 1) * tam;
    const itens = lista.slice(inicio, inicio + tam);

    return {
        itens,
        pagina: atual,
        totalPaginas,
        de: total ? inicio + 1 : 0,
        ate: total ? inicio + itens.length : 0,
        total,
    };
}
